// src/components/home/HomeEndOfDay.tsx
import React from 'react';
import { Euro, Users, CalendarClock, UserX } from 'lucide-react';
import Modal from '../ui/Modal';
import { getInitials } from '@/lib/avatarUtils';

export type StaffProduction = {
    id: string;
    name: string;
    revenue: number;
    count: number;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    todayRevenue: number;
    todayCount: number;
    perStaff: StaffProduction[];
    tomorrowCount: number;
    tomorrowRevenue: number;
    noShowCount: number;
};

const euro = (n: number) => `€ ${Math.round(n).toLocaleString('it-IT')}`;

const HomeEndOfDay: React.FC<Props> = ({
    isOpen,
    onClose,
    todayRevenue,
    todayCount,
    perStaff,
    tomorrowCount,
    tomorrowRevenue,
    noShowCount,
}) => {
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            headerContent={
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Fine giornata</h2>
                    <p className="text-gray-500 text-sm mt-1">Il riepilogo di oggi in 30 secondi</p>
                </div>
            }
        >
            <div className="space-y-5">
                {/* Incasso di oggi */}
                <div className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6">
                    <div className="flex items-center gap-2 text-gray-300 text-xs font-bold uppercase tracking-wider">
                        <Euro size={14} /> Incasso di oggi
                    </div>
                    <p className="text-4xl font-bold mt-2">{euro(todayRevenue)}</p>
                    <p className="text-sm text-gray-400 mt-1">{todayCount} appuntamenti completati</p>
                </div>

                {/* Produzione per staff */}
                <div>
                    <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider mb-3">
                        <Users size={14} /> Produzione per collaboratore
                    </div>
                    {perStaff.length === 0 ? (
                        <p className="text-sm text-gray-400">Nessuna produzione registrata oggi.</p>
                    ) : (
                        <div className="space-y-2">
                            {perStaff.map((s) => (
                                <div key={s.id} className="flex items-center gap-3 p-3 rounded-2xl border border-gray-100">
                                    <div className="w-9 h-9 rounded-full bg-clip-50 text-clip-600 flex items-center justify-center text-xs font-bold uppercase shrink-0">
                                        {getInitials(s.name)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-800 text-sm truncate">{s.name}</p>
                                        <p className="text-xs text-gray-400">{s.count} appuntamenti</p>
                                    </div>
                                    <span className="font-bold text-gray-900 text-sm">{euro(s.revenue)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Domani + No-show */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-gray-100 p-4">
                        <div className="flex items-center gap-2 text-clip-500 text-xs font-bold uppercase tracking-wider">
                            <CalendarClock size={14} /> Domani
                        </div>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{tomorrowCount}</p>
                        <p className="text-xs text-gray-400">appuntamenti · {euro(tomorrowRevenue)} previsti</p>
                    </div>
                    <div className={`rounded-2xl border p-4 ${noShowCount > 0 ? 'border-red-100 bg-red-50/40' : 'border-gray-100'}`}>
                        <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${noShowCount > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                            <UserX size={14} /> No-show oggi
                        </div>
                        <p className={`text-2xl font-bold mt-1 ${noShowCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>{noShowCount}</p>
                        <p className="text-xs text-gray-400">
                            {noShowCount > 0 ? 'mancate presentazioni' : 'nessuna assenza'}
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default HomeEndOfDay;
